/**
 * Évaluateur de conditions pour l'affichage des questions
 * Gère la logique conditionnelle côté frontend
 */

import type { QuestionDefinition, QuestionConditions } from '@/data/questions-database';
import type { QuestionAnswer } from '@/types/frontend';

export interface AnalysisData {
  errors_detected?: Array<{ type: string; severity: string; message: string }>;
  opportunities?: Array<{ type: string; description: string }>;
  factors_analysis?: Record<string, { level: string; impact: string }>;
}

export interface DPEData {
  [key: string]: any;
}

export class QuestionConditionEvaluator {
  /**
   * Détermine si une question doit être affichée
   */
  static shouldShowQuestion(
    question: QuestionDefinition,
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): boolean {
    console.log(`=== DEBUG_FRONTEND_CONDITIONS: Evaluating question ${question.id} ===`);
    
    // 1. Questions critiques toujours affichées
    if (this.isAlwaysShowCategory(question.category)) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - ALWAYS SHOW category: ${question.category}`);
      return true;
    }

    // 2. Pas de conditions = toujours affichée
    if (!question.show_conditions) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - NO CONDITIONS, showing`);
      return true;
    }

    const conditions = question.show_conditions;
    console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - has conditions:`, conditions);

    // 3. Vérifier les réponses requises
    if (!this.checkRequiredAnswers(conditions, currentAnswers)) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - HIDDEN: required answers not met`);
      return false;
    }

    // 4. Vérifier les conditions de masquage
    if (this.checkHideConditions(conditions, currentAnswers)) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - HIDDEN: hide conditions met`);
      return false;
    }

    // 5. Vérifier les erreurs DPE requises
    if (!this.checkRequiredErrors(conditions, analysisData)) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - HIDDEN: required errors not met`);
      return false;
    }

    // 6. Vérifier les opportunités requises
    if (!this.checkRequiredOpportunities(conditions, analysisData)) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - HIDDEN: required opportunities not met`);
      return false;
    }

    // 7. Vérifier les facteurs d'analyse requis
    if (!this.checkRequiredFactors(conditions, analysisData)) {
      console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - HIDDEN: required factors not met`);
      return false;
    }

    console.log(`DEBUG_FRONTEND_CONDITIONS: Question ${question.id} - VISIBLE: all conditions met`);
    return true;
  }

  /**
   * Vérifie si la catégorie doit toujours être affichée
   */
  private static isAlwaysShowCategory(category: string): boolean {
    const ALWAYS_SHOW_CATEGORIES = [
      'BUSINESS', 
      'VERIFICATION', // ✅ Catégorie critique toujours affichée
      'TECHNIQUE' // ✅ Questions techniques importantes toujours affichées
    ];
    return ALWAYS_SHOW_CATEGORIES.includes(category);
  }

  /**
   * Vérifie si les réponses requises sont présentes
   */
  private static checkRequiredAnswers(
    conditions: QuestionConditions,
    currentAnswers: QuestionAnswer[]
  ): boolean {
    if (!conditions.required_answers) return true;

    // TOUTES les réponses requises doivent être présentes (ET logique, pas OU)
    return conditions.required_answers.every(questionId =>
      currentAnswers.find(a => a.question_id === questionId)
    );
  }

  /**
   * Vérifie les conditions de masquage
   */
  private static checkHideConditions(
    conditions: QuestionConditions,
    currentAnswers: QuestionAnswer[]
  ): boolean {
    // Vérifier hide_if_answer (condition unique)
    if (conditions.hide_if_answer) {
      const hideCondition = conditions.hide_if_answer;
      const blockingAnswer = currentAnswers.find(a =>
        a.question_id === hideCondition.question_id &&
        a.selected_option_id === hideCondition.option_id
      );
      if (blockingAnswer) return true;
    }

    // Vérifier hide_if_answers (conditions multiples)
    if (conditions.hide_if_answers && conditions.hide_if_answers.length > 0) {
      const hasBlockingAnswer = conditions.hide_if_answers.some(hideCondition =>
        currentAnswers.find(a =>
          a.question_id === hideCondition.question_id &&
          a.selected_option_id === hideCondition.option_id
        )
      );
      if (hasBlockingAnswer) return true;
    }

    return false;
  }

  /**
   * Vérifie si les erreurs DPE requises sont présentes
   */
  private static checkRequiredErrors(
    conditions: QuestionConditions,
    analysisData: AnalysisData
  ): boolean {
    if (!conditions.required_errors) return true;

    return conditions.required_errors.some(errorType =>
      analysisData?.errors_detected?.find(e => e.type === errorType)
    );
  }

  /**
   * Vérifie si les opportunités requises sont présentes
   */
  private static checkRequiredOpportunities(
    conditions: QuestionConditions,
    analysisData: AnalysisData
  ): boolean {
    if (!conditions.required_opportunities) return true;

    return conditions.required_opportunities.some(opportunityType =>
      analysisData?.opportunities?.find(o => o.type === opportunityType)
    );
  }

  /**
   * Vérifie si les facteurs d'analyse requis sont présents
   */
  private static checkRequiredFactors(
    conditions: QuestionConditions,
    analysisData: AnalysisData
  ): boolean {
    if (!conditions.required_factors) return true;

    return conditions.required_factors.some(factorCondition => {
      const factor = analysisData?.factors_analysis?.[factorCondition.factor];
      return factor && factorCondition.level.includes(factor.level);
    });
  }

  /**
   * Filtre une liste de questions selon les conditions
   */
  static filterQuestions(
    questions: QuestionDefinition[],
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): QuestionDefinition[] {
    return questions.filter(question =>
      this.shouldShowQuestion(question, currentAnswers, dpeData, analysisData)
    );
  }

  /**
   * Obtient la prochaine question non répondue et visible
   */
  static getNextQuestion(
    questions: QuestionDefinition[],
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): QuestionDefinition | null {
    const visibleQuestions = this.filterQuestions(questions, currentAnswers, dpeData, analysisData);
    
    // Trouver la première question non répondue
    const unansweredQuestion = visibleQuestions.find(question =>
      !currentAnswers.find(a => a.question_id === question.id)
    );

    return unansweredQuestion || null;
  }

  /**
   * Calcule le prochain index de question en tenant compte des conditions
   */
  static getNextQuestionIndex(
    currentIndex: number,
    questions: QuestionDefinition[],
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): number {
    let nextIndex = currentIndex + 1;

    // Parcourir les questions suivantes pour trouver la prochaine visible
    while (nextIndex < questions.length) {
      const question = questions[nextIndex];
      
      if (this.shouldShowQuestion(question, currentAnswers, dpeData, analysisData)) {
        return nextIndex;
      }
      
      nextIndex++;
    }

    // Aucune question suivante trouvée
    return -1;
  }

  /**
   * Calcule le progrès du questionnaire
   */
  static calculateProgress(
    questions: QuestionDefinition[],
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): {
    totalVisible: number;
    answered: number;
    percentage: number;
  } {
    const visibleQuestions = this.filterQuestions(questions, currentAnswers, dpeData, analysisData);
    const answeredCount = visibleQuestions.filter(question =>
      currentAnswers.find(a => a.question_id === question.id)
    ).length;

    return {
      totalVisible: visibleQuestions.length,
      answered: answeredCount,
      percentage: visibleQuestions.length > 0 ? Math.round((answeredCount / visibleQuestions.length) * 100) : 0
    };
  }

  /**
   * Vérifie si toutes les questions requises visibles sont répondues
   */
  static areRequiredQuestionsAnswered(
    questions: QuestionDefinition[],
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): boolean {
    const visibleQuestions = this.filterQuestions(questions, currentAnswers, dpeData, analysisData);
    const requiredQuestions = visibleQuestions.filter(q => q.required);
    
    return requiredQuestions.every(q =>
      currentAnswers.some(a => a.question_id === q.id)
    );
  }

  /**
   * Debug: Affiche les raisons pour lesquelles une question est masquée
   */
  static debugQuestionVisibility(
    question: QuestionDefinition,
    currentAnswers: QuestionAnswer[],
    dpeData: DPEData,
    analysisData: AnalysisData
  ): {
    visible: boolean;
    reasons: string[];
  } {
    const reasons: string[] = [];
    
    if (this.isAlwaysShowCategory(question.category)) {
      reasons.push('Catégorie toujours affichée');
      return { visible: true, reasons };
    }

    if (!question.show_conditions) {
      reasons.push('Aucune condition définie');
      return { visible: true, reasons };
    }

    const conditions = question.show_conditions;

    // Vérifier chaque condition
    if (conditions.required_answers && !this.checkRequiredAnswers(conditions, currentAnswers)) {
      reasons.push(`Réponses requises manquantes: ${conditions.required_answers.join(', ')}`);
    }

    if (this.checkHideConditions(conditions, currentAnswers)) {
      if (conditions.hide_if_answer) {
        reasons.push(`Masquée par réponse: ${conditions.hide_if_answer.question_id} = ${conditions.hide_if_answer.option_id}`);
      }
      if (conditions.hide_if_answers) {
        const matchingConditions = conditions.hide_if_answers
          .filter(hc => currentAnswers.find(a =>
            a.question_id === hc.question_id && a.selected_option_id === hc.option_id
          ))
          .map(hc => `${hc.question_id} = ${hc.option_id}`);
        if (matchingConditions.length > 0) {
          reasons.push(`Masquée par l'une de ces réponses: ${matchingConditions.join(', ')}`);
        }
      }
    }

    if (conditions.required_errors && !this.checkRequiredErrors(conditions, analysisData)) {
      reasons.push(`Erreurs DPE requises manquantes: ${conditions.required_errors.join(', ')}`);
    }

    if (conditions.required_opportunities && !this.checkRequiredOpportunities(conditions, analysisData)) {
      reasons.push(`Opportunités requises manquantes: ${conditions.required_opportunities.join(', ')}`);
    }

    if (conditions.required_factors && !this.checkRequiredFactors(conditions, analysisData)) {
      reasons.push(`Facteurs requis manquants: ${JSON.stringify(conditions.required_factors)}`);
    }

    const visible = reasons.length === 0;
    return { visible, reasons };
  }
}

export default QuestionConditionEvaluator;